import { notification } from "antd";

import { SettingApi } from "lib/api";
import { ApiHelper, SignalR } from "lib/api/base";
import { LoginSession, User } from "lib/data/model";
import { SessionStatus } from "lib/data/model/enumerations";
import { LoginService } from "lib/services";


export class SessionService {

	public static readonly instance: SessionService = new SessionService();

	private readonly url: string;

	private readonly notificationTimeInMinutes = 5;
	private readonly notificationRepeatTimeInSeconds = 60;

	private expirationTimout?: number;
	private notificationTimout?: number;
	private notificationInterval?: number;

	private constructor() {

		// Set Hub Url
		this.url = `${SettingApi.getApiBaseUrl()}/Session`;

		// Initialize Service After Timeout
		setTimeout(this.initialize, 500);
	}

	public setSessionUser = async (user: User) => {

		const currentUser = await LoginService.getUserIfExists();

		if (currentUser && currentUser.id === user.id) {

			const session = this.getSession();

			if (session !== undefined) {

				session.user = user;

				this.setSession(session);
			}
		}
	}

	public registerSignalR = async () => {

		await SignalR.register(this.url);
		await SignalR.listen(this.url, "ReceiveSession", this.onReceiveSession);
		await SignalR.start(this.url);
	}

	public unregisterSignalR = async () => {

		await SignalR.stop(this.url);
	}

	public isSignalRRegistered = async (): Promise<boolean> => {

		// Get Base Url
		const baseUrl = await SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Return Whether Registered
			return SignalR.isRegistered(this.url);
		}

		// Return NOT Registered
		return false;
	}

	public setTimer = async (session: LoginSession) => {

		// Clear Any Previous Timers
		this.clearTimer();

		// Calculate Session Remaining
		const sessionRemaining = (session.expiresAt.getTime() - Date.now());

		// Check Session
		if (session.status !== SessionStatus.Active || sessionRemaining <= 0) {

			// Ensure We Are Still Signed In
			const isLoggedIn = await LoginService.isLoggedIn();

			// Check Login
			if (isLoggedIn) {

				// Logout
				await LoginService.logout();
			}
		}
		else {

			// Set Expiration Timer
			this.expirationTimout = setTimeout(
				async () => {

					// Logout
					await LoginService.logout();

					// Clear Timers
					this.clearTimer();
				},
				sessionRemaining
			);

			// Convert Set Notification Time In Minutes To Milliseconds
			const notificationTimeInMilliseconds = this.notificationTimeInMinutes * 60 * 1000;

			// Set Notification Time
			const notificationTime = (sessionRemaining < notificationTimeInMilliseconds ? 0 : sessionRemaining - notificationTimeInMilliseconds);
			const sessionRemainingInMinutes = (sessionRemaining - notificationTime) / 1000 / 60;

			// Set Notification Timer
			this.notificationTimout = setTimeout(
				() => {

					// Close Any Previous Notifications
					notification.close("session-timeout");
					notification.close("session-interval");

					notification.warning({
						message: "Session Expiring",
						key: "session-timeout",
						description: `Your session will expire in under ${Math.ceil(sessionRemainingInMinutes)} minute(s).  Perform any action (including navigation) to automatically extend your session.`,
						duration: 7.5
					});

					// Check If Session Is Expiring In Less Than One Minute Already
					if (sessionRemainingInMinutes > 1) {

						// Set New Minutes Remaining
						let newSessionRemainingInMinutes = sessionRemainingInMinutes - (this.notificationRepeatTimeInSeconds / 60);

						// Set Notification Interval
						this.notificationInterval = setInterval(
							() => {

								// Reset Minutes Remaining
								newSessionRemainingInMinutes -= (this.notificationRepeatTimeInSeconds / 60);

								// Close Any Previous Internal Notifications
								notification.close("session-interval");

								notification.warning({
									message: "Session Expiring",
									key: "session-interval",
									description: `Your session will expire in under ${Math.ceil(newSessionRemainingInMinutes)} minute(s).  Perform any action (including navigation) to automatically extend your session.`,
									duration: 7.5
								});

								// Check Session Remaining
								if (newSessionRemainingInMinutes <= 1) {

									// Clear Self
									clearInterval(this.notificationInterval!);
									this.notificationInterval = undefined;
								}
							},
							(this.notificationRepeatTimeInSeconds * 1000)
						);
					}

					// Clear Self
					clearTimeout(this.notificationTimout!);
					this.notificationTimout = undefined;

				},
				notificationTime
			);
		}
	}

	public clearTimer = () => {

		// Check For Previous Expiration Timer
		if (this.expirationTimout !== undefined) {

			// Clear Previous Expiration Timer
			clearTimeout(this.expirationTimout);

			// Remove Timer
			this.expirationTimout = undefined;
		}

		// Check For Previous Notification Timer
		if (this.notificationTimout !== undefined) {

			// Clear Previous Notification Timer
			clearTimeout(this.notificationTimout);

			// Remove Timer
			this.notificationTimout = undefined;
		}

		// Check For Previous Notification Interval Timer
		if (this.notificationInterval !== undefined) {

			// Clear Previous Notification Interval Timer
			clearInterval(this.notificationInterval);

			// Remove Timer
			this.notificationInterval = undefined;
		}

		// Close Any Previous Timout Notifications
		notification.close("session-timeout");

		// Close Any Previous Internal Notifications
		notification.close("session-interval");
	}

	public getSession = (): LoginSession | undefined => {

		// Get Session From Storage
		const sessionJson = sessionStorage.getItem("this.session");

		// Check Session
		if (sessionJson !== null) {

			// Parse Session
			const session = JSON.parse(sessionJson) as LoginSession;

			// Process Session Dates
			ApiHelper.parseDateTimeString(session, "startedAt", "expiresAt", "endedAt");

			// Return Session
			return session;
		}
		else {

			// Return Nothing
			return undefined;
		}
	}

	public setSession = (session: LoginSession) => {

		// Store Session In Storage
		sessionStorage.setItem("this.session", JSON.stringify(session));
	}

	public clearSession = () => {

		// Store Session In Storage
		sessionStorage.removeItem("this.session");
	}

	/////////////////////////
	//// Private Methods ////
	/////////////////////////

	private initialize = async () => {

		// Check If Already Registered
		if (!SignalR.isRegistered(this.url)) {

			// Get Whether Logged In
			const isLoggedIn = await LoginService.isLoggedIn();

			// Check Whether Logged In
			if (isLoggedIn) {

				// Register SignalR
				await this.registerSignalR();
			}
		}
	}

	private onReceiveSession = (session: LoginSession) => {

		// Process Session Dates
		ApiHelper.parseDateTimeString(session, "startedAt", "expiresAt", "endedAt");

		// Set Session In Storage
		this.setSession(session);

		// Set Timers
		this.setTimer(session);
	}
}
